import { useEffect, useState } from "react";
import { MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { textFeaturesApi, type TextFeature } from "../../../api/textFeatures";
import { Button, Field, TextInput } from "../../../components/ui";

function Row({ f, code, busy, onFlip }: { f: TextFeature; code: string; busy: boolean; onFlip: (f: TextFeature) => void }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-4 border-t border-white/10 py-4 first:border-t-0" data-testid={`text-feature-${f.key}`}>
      <div className="min-w-0">
        <p className="font-medium text-white">{f.label}</p>
        <p className="mt-1 text-xs text-zinc-500">{f.note}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${f.enabled ? "text-emerald-300" : "text-zinc-400"}`} data-testid={`text-feature-state-${f.key}`}>{f.enabled ? "On" : "Off"}</span>
        <Button variant="secondary" className="h-9 px-4 text-[11px]" loading={busy} disabled={code.length < 6} onClick={() => onFlip(f)} data-testid={`text-feature-toggle-${f.key}`}>
          Turn {f.enabled ? "off" : "on"}</Button>
      </div>
    </li>
  );
}

export function TextFeaturesCard() {
  const [features, setFeatures] = useState<TextFeature[] | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { textFeaturesApi.status().then((r) => setFeatures(r.features)).catch((e) => toast.error(errorMessage(e))); }, []);
  const flip = async (f: TextFeature) => {
    setBusy(true);
    try { setFeatures((await textFeaturesApi.set(f.key, !f.enabled, code)).features); setCode(""); toast.success(`${f.label} ${f.enabled ? "off" : "on"}`); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-sm border border-white/10 bg-surface p-6" data-testid="text-features-card">
      <h2 className="flex items-center gap-2 text-lg font-medium text-white"><MessageSquareText className="h-5 w-5 text-signal" />Text features</h2>
      <p className="mt-1 text-sm text-zinc-400">Texts the app sends on its own. Crew text codes and their replies always work.</p>
      <div className="mt-4 max-w-sm"><Field label="Current 6-digit code or a backup code" htmlFor="text-features-code">
        <TextInput id="text-features-code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="text-features-code-input" /></Field></div>
      <ul className="mt-2">{features?.map((f) => <Row key={f.key} f={f} code={code} busy={busy} onFlip={flip} />)}</ul>
    </section>
  );
}
